import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from '@angular/router';
import { zip } from 'rxjs';
import { User } from "../../../auth/models";
import { Validators } from "../../../common/forms";
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Contract, ContractSupplier, Round, ROUND_SUPPLIER_STATUSES, Tender } from '../../models';
import { ROUND_STATUSES } from '../../models/round';
import { AWAITING_OFFER_STATUSES } from "../../models/round-supplier/round-supplier.statuses";
import {
    TenderBidCopyToCurrentRoundService,
    TenderContractsService,
    TenderContractSuppliersService,
    TenderService
} from '../../services';

@Component({
    selector: 'jft-tender-contract-dashboard',
    templateUrl: './tender-contract-dashboard.component.html',
    styleUrls: ['./tender-contract-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractDashboardComponent implements OnInit {

    public tender: Tender;
    public contract: Contract;
    public rounds: Round[] = [];
    public round: Round;
    public suppliers: ContractSupplier[] = [];
    public offerStatuses = ROUND_SUPPLIER_STATUSES;
    public roundStatuses = ROUND_STATUSES;
    public awaitingOfferStatuses = AWAITING_OFFER_STATUSES;
    public loadingAward: boolean = false;
    public offerNumber: number = 1;
    public roundNum: number;
    public awardedForm: UntypedFormGroup;
    public lastRound: Round;

    public dateNow = new Date();

    constructor(
        public user: User,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService,
        private service: TenderContractSuppliersService,
        private serviceCopyBid: TenderBidCopyToCurrentRoundService,
        private tenderContractService: TenderContractsService,
        private tenderService: TenderService,
        private loadingIndicatorService: LoadingIndicatorService
    ) {
    }

    public ngOnInit() {
        let data = this.route.parent.snapshot.data,
            parentData = this.route.parent.parent.snapshot.data,
            parentParentData = this.route.parent.parent.parent.snapshot.data;

        this.contract = data['contract'];
        this.rounds = data['rounds'];
        this.tender = parentParentData['tender'];
        this.lastRound = this.rounds.slice(-1)[0];
        this.roundNum = this.route.snapshot.params.rid;
        this.round = this.rounds.filter(item => item.round == this.roundNum)[0];

        this.loadingIndicatorService.show();
        this.service.fetch(this.tender.id, this.contract.id, this.round.round).subscribe((data) => {
            this.loadingIndicatorService.hide();
            this.suppliers = data;
        });

        this.createAwardedForm();
    }

    public createAwardedForm(data: any = null) {
        this.awardedForm = new UntypedFormGroup({
            percent_awarded: new UntypedFormControl(
                null,
                [Validators.integer, Validators.required, Validators.min(1), Validators.max(100)]),
        });
    }

    public getFormGroupErrorClasses(control: AbstractControl, isInvalid: boolean = false): {} {
        if (isInvalid) {
            return {'is-invalid': control.invalid};
        }
        return {'is-invalid': control.invalid && control.touched};
    }

    public showErrorBlock(control: AbstractControl): boolean {
        return control.errors && (control.dirty || control.touched);
    }

    public award(supplier: ContractSupplier) { // todo refactor separate award action and update data action
        this.loadingAward = true;
        this.service.award(this.tender, this.contract, supplier).subscribe(() => {

            zip(
                this.service.fetch(this.tender.id, this.contract.id),
                this.tenderContractService.renew(this.tender, this.contract),
                this.tenderService.renew(this.tender)
            ).subscribe((data) => {
                this.suppliers = data[0];
                this.contract = data[1];
                this.tender = data[2];
                this.round = this.contract.currentRound;
                this.loadingAward = false;
                this.toast.success(`Location: ${this.contract.location.getLabel()} awarded`);
            });

        }, (e) => {
            supplier.awardedPercent = null;
            this.toast.error(e);
            this.loadingAward = false;
        });
    }

    public withdrawBid(supplier: ContractSupplier) {
        this.loadingAward = true;
        this.service.withdrawBid(this.tender, this.contract, supplier).subscribe(() => {

            zip(
                this.service.fetch(this.tender.id, this.contract.id),
                this.tenderContractService.renew(this.tender, this.contract),
                this.tenderService.renew(this.tender)
            ).subscribe((data) => {
                this.suppliers = data[0];
                this.contract = data[1];
                this.tender = data[2];
                this.round = this.contract.currentRound;
                this.loadingAward = false;
                this.toast.success(`Bid withdrawn for location: ${this.contract.location.getLabel()}`);
            });

        }, (e) => {
            this.toast.error(e);
            this.loadingAward = false;
        });
    }

    public getOfferName(index, offer, offers) {
        if (index == 0) {
            this.offerNumber = 1;
        } else if (offers[index - 1].supplier.id != offer.supplier.id) {
            this.offerNumber = 1;
        } else {
            this.offerNumber += 1;
        }
        return this.offerNumber;
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }

    public isSubmittedBid() {
        return !!(this.suppliers.filter(item => item.bid ? item.bid : 0)).length;
    }

    public roundChanged(round) {
        this.round = round;
        this.suppliers = [];
        this.router.navigate(['round', this.round.round], { relativeTo: this.route.parent });
    }

    public onRoundExtend(round: Round) {
        this.roundChanged(round);
    }

    public changeRound(round: Round) {
        this.roundChanged(round);
    }

    public isCurrentRound() {
        return this.round && this.round.round == this.contract.currentRound.round;
    }

    public moveToCurrentRound(offer: ContractSupplier) {
        this.serviceCopyBid.copy(this.tender, this.contract, offer.id).subscribe(item => {
            this.toast.success('Offer has been copied to current round');
            this.changeRound(this.lastRound);
        }, error => {
            this.toast.error(error);
        })
    }
}
